// API-Tokens: Liste, Erzeugen (Klartext einmalig), Widerrufen. Wiederverwendet für persönliche und Bot-Tokens.
import type { ApiScope, ApiTokenCreatedDto, BotDto } from '@lagerfeuer/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { tokensApi } from '../api';
import { displayName, formatDateTime } from '../format';
import { toast } from '../toast';
import { Badge, Button, ConfirmButton, Dialog, Empty, ErrorBox, Field, Loading, SecretReveal } from '../ui';
import { ALL_SCOPES, botsErrorText, expiryToIso, isExpired, SCOPE_LABELS, tokensKey, toggleScope, type ExpiryChoice } from './bots-utils';

/** Tabelle der Tokens eines Besitzers (ohne ownerId = eigene persönliche Tokens). */
export function TokenList(props: { ownerId?: string; testId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: tokensKey(props.ownerId), queryFn: () => tokensApi.list(props.ownerId) });
  const revoke = useMutation({
    mutationFn: (id: string) => tokensApi.revoke(id),
    onSuccess: () => {
      toast({ kind: 'success', title: 'Token widerrufen' });
      void qc.invalidateQueries({ queryKey: ['extras', 'tokens'] });
    },
  });

  if (q.isPending) return <Loading testId={`${props.testId}-loading`} />;
  if (q.isError) return <ErrorBox error={q.error} testId={`${props.testId}-error`} />;
  if (!q.data.length) return <Empty testId={`${props.testId}-empty`}>Noch keine Tokens.</Empty>;

  return (
    <div className="lfx-stack">
      {revoke.isError && <ErrorBox error={revoke.error} testId={`${props.testId}-revoke-error`} />}
      <ul className="lfx-bt-list" data-testid={props.testId}>
        {q.data.map((t) => {
          const expired = isExpired(t.expiresAt);
          return (
            <li key={t.id} className={`lfx-bt-token${expired ? ' lfx-bt-token--expired' : ''}`} data-testid={`${props.testId}-row`} data-token-id={t.id}>
              <div className="lfx-row lfx-row--between">
                <div className="lfx-grow">
                  <strong data-testid="bots-token-name">{t.name}</strong>{' '}
                  <code className="lfx-mono lfx-muted" data-testid="bots-token-prefix">
                    {t.prefix}…
                  </code>
                </div>
                <ConfirmButton
                  label="Widerrufen"
                  confirmLabel="Wirklich widerrufen?"
                  testId={`bots-token-revoke-${t.id}`}
                  disabled={revoke.isPending}
                  onConfirm={() => revoke.mutate(t.id)}
                />
              </div>
              <div className="lfx-row" aria-label="Berechtigungen">
                {t.scopes.map((s) => (
                  <Badge key={s} tone={s === '*' ? 'accent' : 'neutral'} title={SCOPE_LABELS[s]} testId="bots-token-scope">
                    {s}
                  </Badge>
                ))}
              </div>
              <div className="lfx-bt-meta lfx-muted">
                <span>Erstellt: {formatDateTime(t.createdAt)}</span>
                <span>Zuletzt benutzt: {t.lastUsedAt ? formatDateTime(t.lastUsedAt) : 'nie'}</span>
                <span data-testid="bots-token-expiry">
                  Ablauf: {t.expiresAt ? formatDateTime(t.expiresAt) : 'nie'}{' '}
                  {expired && (
                    <Badge tone="danger" testId="bots-token-expired">
                      abgelaufen
                    </Badge>
                  )}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Dialog „Token erstellen": Name, Scopes, Ablauf, optional Besitzer-Bot. Zeigt danach den Klartext einmalig. */
export function TokenCreateDialog(props: { onClose: () => void; bots?: BotDto[]; fixedOwner?: BotDto }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiScope[]>([]);
  const [expiry, setExpiry] = useState<ExpiryChoice>('never');
  const [date, setDate] = useState('');
  const [ownerId, setOwnerId] = useState(props.fixedOwner?.user.id ?? '');
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<ApiTokenCreatedDto | null>(null);

  const create = useMutation({
    gcTime: 0, // Einmal-Geheimnis nicht im Mutation-Cache liegen lassen
    mutationFn: tokensApi.create,
    onSuccess: (tok) => {
      setCreated(tok);
      void qc.invalidateQueries({ queryKey: ['extras', 'tokens'] });
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!name.trim()) return setFormError('Bitte einen Namen angeben.');
    if (!scopes.length) return setFormError('Bitte mindestens eine Berechtigung wählen.');
    let expiresAt: string | undefined;
    try {
      expiresAt = expiryToIso(expiry, date);
    } catch (err) {
      return setFormError((err as Error).message);
    }
    create.mutate({ name: name.trim(), scopes, ...(expiresAt ? { expiresAt } : {}), ...(ownerId ? { ownerId } : {}) });
  };

  if (created) {
    return (
      <Dialog
        title="Token erstellt"
        testId="bots-token-created"
        onClose={props.onClose}
        footer={
          <Button variant="primary" testId="bots-token-created-done" onClick={props.onClose}>
            Fertig
          </Button>
        }
      >
        <SecretReveal label={`Token „${created.name}"`} value={created.token} testId="bots-token-secret" />
        <div className="lfx-muted">Nutzung als HTTP-Header bzw. im Socket-Handshake:</div>
        <pre className="lfx-bt-code" data-testid="bots-token-usage">{`Authorization: Bot ${created.token}`}</pre>
      </Dialog>
    );
  }

  const ownerBots = props.bots ?? [];
  return (
    <Dialog
      title={props.fixedOwner ? `Token für ${displayName(props.fixedOwner.user)}` : 'API-Token erstellen'}
      testId="bots-token-dialog"
      onClose={props.onClose}
    >
      <form className="lfx-stack" onSubmit={submit} data-testid="bots-token-form" noValidate>
        <Field label="Name">
          {(id) => (
            <input
              id={id}
              className="lfx-input"
              data-testid="bots-token-name-input"
              value={name}
              maxLength={100}
              onChange={(e) => setName(e.target.value)}
              placeholder="z. B. Statistik-Skript"
            />
          )}
        </Field>
        <fieldset className="lfx-bt-fieldset" data-testid="bots-token-scopes">
          <legend className="lfx-field__label">Berechtigungen</legend>
          {ALL_SCOPES.map((s) => (
            <label key={s} className="lfx-check lfx-bt-scope">
              <input
                type="checkbox"
                data-testid={`bots-token-scope-${s === '*' ? 'all' : s}`}
                checked={scopes.includes(s)}
                onChange={(e) => setScopes((cur) => toggleScope(cur, s, e.target.checked))}
              />
              <code className="lfx-mono">{s}</code>
              <span className="lfx-muted">{SCOPE_LABELS[s]}</span>
            </label>
          ))}
        </fieldset>
        <Field label="Ablauf">
          {(id) => (
            <select id={id} className="lfx-select" data-testid="bots-token-expiry-select" value={expiry} onChange={(e) => setExpiry(e.target.value as ExpiryChoice)}>
              <option value="never">Läuft nie ab</option>
              <option value="7">7 Tage</option>
              <option value="30">30 Tage</option>
              <option value="90">90 Tage</option>
              <option value="date">Bestimmtes Datum …</option>
            </select>
          )}
        </Field>
        {expiry === 'date' && (
          <Field label="Ablaufdatum">
            {(id) => <input id={id} type="date" className="lfx-input" data-testid="bots-token-expiry-date" value={date} onChange={(e) => setDate(e.target.value)} />}
          </Field>
        )}
        {!props.fixedOwner && ownerBots.length > 0 && (
          <Field label="Besitzer" hint="Optional: Token im Namen eines deiner Bots ausstellen.">
            {(id) => (
              <select id={id} className="lfx-select" data-testid="bots-token-owner-select" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
                <option value="">Ich selbst</option>
                {ownerBots.map((b) => (
                  <option key={b.user.id} value={b.user.id}>
                    Bot: {displayName(b.user)} (@{b.user.username})
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
        <p className="lfx-muted lfx-bt-note">Tokens erhalten nie Plattform-Admin-Rechte, auch nicht mit Vollzugriff.</p>
        {formError && (
          <div className="lfx-error" role="alert" data-testid="bots-token-form-error">
            {formError}
          </div>
        )}
        {create.isError && (
          <div className="lfx-error" role="alert" data-testid="bots-token-create-error">
            {botsErrorText(create.error, 'token')}
          </div>
        )}
        <div className="lfx-row lfx-bt-actions">
          <Button variant="ghost" testId="bots-token-cancel" onClick={props.onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" testId="bots-token-submit" disabled={create.isPending}>
            {create.isPending ? 'Erstelle …' : 'Token erstellen'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Abschnitt „Persönliche API-Tokens". */
export function PersonalTokensSection(props: { bots?: BotDto[] }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="lfx-card lfx-stack" data-testid="bots-tokens-section" aria-labelledby="lfx-bt-tokens-title">
      <div className="lfx-row lfx-row--between">
        <h2 id="lfx-bt-tokens-title" className="lfx-bt-title">
          Persönliche API-Tokens
        </h2>
        <Button variant="primary" testId="bots-token-create" onClick={() => setOpen(true)}>
          Token erstellen
        </Button>
      </div>
      <p className="lfx-muted lfx-bt-note">
        Mit einem Token greifen Skripte und Werkzeuge über die API in deinem Namen zu (Header <code className="lfx-mono">Authorization: Bot &lt;token&gt;</code>).
        Tokens haben nie Plattform-Admin-Rechte.
      </p>
      <TokenList testId="bots-token-list" />
      {open && <TokenCreateDialog bots={props.bots} onClose={() => setOpen(false)} />}
    </section>
  );
}
